import { useAtomValue } from 'jotai'
import { useEffect, useState } from 'react'
import { useAudioSettingsState } from '~/atoms/AudioSettingsState'
import { getAuthStateFromLocalStorage } from '~/atoms/AuthenticationState'
import {
  type ConnectionEvent,
  connectionEventsAtom,
  connectionStatusAtom,
  lastWebSocketPingAtom,
} from '~/atoms/ConnectionState'
import { useSettingsState } from '~/atoms/SettingsState'
import { useUserState } from '~/atoms/UserState'
import { activeCallUuidAtom, callListAtom } from '~/atoms/WebphoneState'
import { useClientUpdateAvailableCheck } from '~/hooks/useCheckClientVersion'
import { useOnlineStatus } from '~/hooks/useOnlineStatus'
import {
  registrationErrorAtom,
  registrationStatusAtom,
  transportConnectionStateAtom,
} from '~/hooks/usePhone'
import WebSocketService from '~/services/WebSocketService'
import { appVersion, buildEnvironment, isElectron } from '~/utils/common'
import { getAccessTokenExpiryMs } from '~/utils/functions'

export type HealthLevel = 'healthy' | 'degraded' | 'down' | 'idle' | 'unknown'

export interface DiagnosticsSnapshot {
  // Connection
  network: { online: boolean }
  sip: {
    registrationState: string
    registrationError: string | null
    transportState: string
  }
  websocket: {
    connectionStatus: string
    // Epoch-ms of the last heartbeat received, or null if none this session.
    lastPingAt: number | null
    // Reconnect attempts since the last successful connection (0 = healthy).
    reconnectAttempts: number
  }
  // Audio (no media content — device labels + permission only)
  audio: {
    micPermission: string
    inputCount: number
    outputCount: number
    selectedInput: string | null
    selectedOutput: string | null
  }
  // Active calls — METADATA ONLY. Deliberately excludes numbers, names,
  // caller ID, and contact info so the copied payload stays non-sensitive.
  call: {
    activeCount: number
    hasActiveCall: boolean
    active: {
      status: string
      direction: string | null
      source: string | null
      isOnHold: boolean
      isMuted: boolean
      durationSeconds: number | null
    } | null
  }
  // Telephony
  telephony: {
    // Internal user ID for support correlation (not PII — matches the
    // identifier already sent to the support chat widget and Cloudwatch
    // logs). Name/email are deliberately excluded.
    userId: number | null
    activeExtension: string | null
    activeExtensionName: string | null
    activeExtensionId: number | null
    extensions: {
      id: number
      number: string
      name: string
      type: string
      isActiveSession: boolean
    }[]
    pbxId: number | null
    pbxUuid: string | null
    pbxName: string | null
    doNotDisturb: boolean
    // The underlying `hasCallWaiting` setting is a documented misnomer:
    // true = "respond with busy to a second call" is enabled (call waiting
    // effectively off). Exposed here under its true meaning.
    respondBusyToSecondCall: boolean
  }
  // Session / auth — no token material, only presence + expiry epoch.
  session: {
    hasToken: boolean
    tokenExpiresAt: number | null
    tokenExpired: boolean
  }
  // Environment
  environment: {
    appVersion: string
    latestVersion: string | null
    isClientOutOfDate: boolean
    buildEnvironment: string
    channel: string | null
    platform: 'electron' | 'web'
    notificationPermission: string
    localStorageWritable: boolean
    userAgent: string
    capturedAt: string
  }
  // Recent connection transitions (kinds + timestamps only, non-sensitive).
  events: ConnectionEvent[]
}

// Whether localStorage can be written — auth-token persistence depends on it,
// and private-mode / storage-blocked browsers are a real support cause.
function checkLocalStorageWritable(): boolean {
  try {
    const k = '__diag_probe__'
    window.localStorage.setItem(k, '1')
    window.localStorage.removeItem(k)
    return true
  } catch {
    return false
  }
}

// Read the access-token expiry (epoch ms) from the JWT without surfacing any
// token material. Returns null if absent/unparseable.
function readTokenExpiry(): {
  hasToken: boolean
  expiresAt: number | null
  expired: boolean
} {
  try {
    const token =
      getAuthStateFromLocalStorage().appSessionJwts?.access_token ?? null
    if (!token) return { hasToken: false, expiresAt: null, expired: false }
    // Opaque/non-JWT tokens report presence without an expiry.
    const expiresAt = getAccessTokenExpiryMs(token)
    // Compute expiry once here so it stays consistent with `expiresAt` rather
    // than re-evaluating Date.now() per render in the snapshot body.
    return {
      hasToken: true,
      expiresAt,
      expired: expiresAt !== null ? expiresAt < Date.now() : false,
    }
  } catch {
    return { hasToken: false, expiresAt: null, expired: false }
  }
}

function getNotificationPermission(): string {
  try {
    return typeof Notification !== 'undefined'
      ? Notification.permission
      : 'unsupported'
  } catch {
    return 'unsupported'
  }
}

// Release channel (prod/beta/etc) on desktop, or 'web' in the browser. Matches
// how useUserState derives it; resolved async from the Electron main process.
function useChannel(): string | null {
  const [channel, setChannel] = useState<string | null>(
    isElectron ? null : 'web',
  )
  useEffect(() => {
    if (!isElectron) return
    let cancelled = false
    window.electron?.channels
      .getCurrentChannel()
      .then((c) => {
        if (!cancelled) setChannel(c)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])
  return channel
}

/**
 * Assembles a single serializable snapshot of the user's phone/connection
 * state from existing client atoms. Drives both the advanced view and the
 * "Copy diagnostics" button so the two never drift. Read-only — pulls atoms
 * directly rather than mounting the full usePhone() hook and its side effects.
 */
export function useDiagnosticsSnapshot(): DiagnosticsSnapshot {
  const online = useOnlineStatus()
  const connectionStatus = useAtomValue(connectionStatusAtom)
  const lastPingAt = useAtomValue(lastWebSocketPingAtom)
  const events = useAtomValue(connectionEventsAtom)
  const registrationState = useAtomValue(registrationStatusAtom)
  const registrationError = useAtomValue(registrationErrorAtom)
  const transportState = useAtomValue(transportConnectionStateAtom)
  const callList = useAtomValue(callListAtom)
  const activeCallUuid = useAtomValue(activeCallUuidAtom)
  const {
    currentUserExtension,
    currentUserExtensionId,
    pbxExtensions,
    pbxId,
    pbx,
    sessionUser,
  } = useUserState()
  const channel = useChannel()
  const {
    settings: {
      webPhone: { doNotDisturbTimestamp, hasCallWaiting },
    },
  } = useSettingsState()
  const { permission, input, output } = useAudioSettingsState()
  const { latestVersion, isClientOutOfDate } = useClientUpdateAvailableCheck()

  // Reconnect count via the static read-only accessor — never constructs the
  // singleton or creates a socket, so it's safe to read during render (the
  // instance getInstance() factory has socket-creation side effects).
  // NOTE: this is a best-effort snapshot, not reactive — it reflects a new
  // value only when something else re-renders the panel (e.g. the heartbeat
  // tick or a status-atom change), which is fine for a diagnostics readout.
  const reconnectAttempts = WebSocketService.getReconnectAttempts()

  // Stamp once when the panel opens rather than on every render, so the
  // displayed "Captured" time and the copied JSON reflect a real moment.
  const [capturedAt] = useState(() => new Date().toISOString())
  // localStorage writability can't change during a session — probe once.
  const [localStorageWritable] = useState(checkLocalStorageWritable)

  // Read token state on every render rather than once on mount: the access
  // token can be refreshed mid-session by the auth interceptor, and a
  // diagnostics readout must show the *current* expiry, not a stale "Expired"
  // captured at open. The parse is cheap (one base64 segment + JSON.parse), so
  // doing it per render — even on the 1s heartbeat tick — is negligible.
  const token = readTokenExpiry()

  const activeCall = activeCallUuid ? callList.get(activeCallUuid) : undefined
  const activeCallDuration =
    activeCall?.startTime != null
      ? Math.max(
          0,
          Math.floor((Date.now() - activeCall.startTime.getTime()) / 1000),
        )
      : null

  return {
    network: { online },
    sip: {
      registrationState: String(registrationState),
      registrationError: registrationError?.message ?? null,
      transportState: String(transportState),
    },
    websocket: {
      connectionStatus,
      lastPingAt,
      reconnectAttempts,
    },
    audio: {
      micPermission: permission,
      inputCount: input.devices.length,
      outputCount: output.devices.length,
      // preference[0] can be undefined (empty preference array); guard so we
      // pass a real string to deviceLabel rather than relying on the no-match
      // path, keeping the call honest with deviceLabel's string signature.
      selectedInput: input.preference[0]
        ? deviceLabel(input.devices, input.preference[0])
        : null,
      selectedOutput: output.preference[0]
        ? deviceLabel(output.devices, output.preference[0])
        : null,
    },
    call: {
      activeCount: callList.size,
      hasActiveCall: activeCall != null,
      active: activeCall
        ? {
            status: String(activeCall.status),
            direction: activeCall.direction ?? null,
            source: activeCall.callSource ?? null,
            isOnHold: activeCall.isOnHold,
            isMuted: activeCall.isMuted,
            durationSeconds: activeCallDuration,
          }
        : null,
    },
    telephony: {
      userId: sessionUser?.id ?? null,
      activeExtension: currentUserExtension?.number ?? null,
      activeExtensionName: currentUserExtension?.name ?? null,
      activeExtensionId: currentUserExtensionId ?? null,
      extensions: (pbxExtensions ?? []).map((ext) => ({
        id: ext.id,
        number: ext.number,
        name: ext.name,
        type: String(ext.type),
        isActiveSession: ext.id === currentUserExtensionId,
      })),
      pbxId: pbxId ?? null,
      pbxUuid: pbx?.uuid ?? null,
      pbxName: pbx?.name ?? null,
      doNotDisturb: doNotDisturbTimestamp != null,
      respondBusyToSecondCall: Boolean(hasCallWaiting),
    },
    session: {
      hasToken: token.hasToken,
      tokenExpiresAt: token.expiresAt,
      tokenExpired: token.expired,
    },
    environment: {
      appVersion,
      latestVersion: latestVersion ?? null,
      isClientOutOfDate,
      buildEnvironment: String(buildEnvironment),
      channel,
      platform: isElectron ? 'electron' : 'web',
      notificationPermission: getNotificationPermission(),
      localStorageWritable,
      userAgent: navigator.userAgent,
      capturedAt,
    },
    events,
  }
}

// Resolve a device's human label from its id; returns null when unknown
// (e.g. the special 'default' id or a device no longer present).
function deviceLabel(
  devices: MediaDeviceInfo[],
  deviceId: string,
): string | null {
  const match = devices.find((d) => d.deviceId === deviceId)
  return match?.label || null
}
